import React, { useCallback, useMemo } from 'react'
import { indicator } from '@groovehq/internal-design-system/lib/styles/elements'
import cn from 'classnames'
import SUI from 'shared/components/ui/SUI'
import KeyboardNavigator from 'components/KeyboardNavigator'
import InvalidSearchMessage from 'components/App/Pages/SearchPage/InvalidSearchMessage'
import Scroller from 'components/Scroller'
import isSearchValid from 'util/search/isSearchValid'
import isQueryStringReady from 'util/search/isQueryStringReady'
import isQueryStringNeedingValue from 'util/search/isQueryStringNeedingValue'
import { propFunc } from 'util/functions'
import { ENTER, TAB } from 'constants/keys'
import Suggestion from './Suggestion'
import SearchFeedback from './SearchFeedback'
import RecentSearches from './RecentSearches'
import styles from './styles.css'
import SearchFor from './SearchFor'

const KEY_TITLE_MAP = {
  narrow: 'Suggested filters',
  modifiers: 'Modifiers',
  keyword: 'Keywords',
  is: 'State',
  my: 'Assigned to me',
  assignee: `Assigned to ${app.t('Agent')}`,
  drafts: `Drafts by ${app.t('Agent')}`,
  group: `Assigned to ${app.t('Group')}`,
  mailbox: app.t('Mailboxes'),
  label: 'Tags',
  folder: 'Folders',
  from: 'From email',
}

const KEYBOARD_NAVIGATOR_KEYS = { select: [ENTER, TAB] }

export default function SearchSuggestions({
  focusElement,
  isSuggestions,
  onSelect,
  onChange,
  onSubmit,
  onMouseDown,
  searchQuery,
  recentSearchQueries,
  suggestions,
  previousLabelSuggestionSize: prevLabelSize,
  className,
  currentSubmittedTicketSearchFilterMatches,
  shouldShowAllSuggestions,
}) {
  const isNeedingValue = isQueryStringNeedingValue(searchQuery)
  const isReady = searchQuery?.trim()
    ? isQueryStringReady(searchQuery)
    : currentSubmittedTicketSearchFilterMatches?.length > 0
  const isInvalidSearch = !isSearchValid(searchQuery) && !isSuggestions
  const handleSubmitBySearchFor = useCallback(
    () => {
      onSubmit(searchQuery, { shouldIncludePreviousSubmittedFilters: true })
    },
    [onSubmit, searchQuery]
  )

  const updatedSuggestions = useMemo(
    () => {
      if (shouldShowAllSuggestions) return suggestions
      return Object.keys(suggestions).reduce((acc, key) => {
        if (key === 'narrow') {
          // eslint-disable-next-line no-param-reassign
          acc[key] = suggestions[key].slice(0, 3)
        } else {
          // eslint-disable-next-line no-param-reassign
          acc[key] = suggestions[key]
        }
        return acc
      }, {})
    },
    [shouldShowAllSuggestions, suggestions]
  )

  return (
    <KeyboardNavigator
      className={cn(styles.SearchSuggestions, className)}
      focusElement={focusElement}
      onMouseDown={onMouseDown}
      activeKey={searchQuery}
      keys={KEYBOARD_NAVIGATOR_KEYS}
      supressDuplicateActivate
    >
      <SUI>
        <Scroller className={styles.SearchSuggestions_scroller}>
          {isInvalidSearch && (
            <InvalidSearchMessage
              searchQuery={searchQuery}
              className="grui pb-10"
            />
          )}
          {isReady && (
            <SearchFor
              onSubmit={handleSubmitBySearchFor}
              searchQuery={searchQuery}
              currentSubmittedTicketSearchFilterMatches={
                currentSubmittedTicketSearchFilterMatches
              }
              className={cn({
                'grui mb-7': !isSuggestions && recentSearchQueries.length === 0,
              })}
            />
          )}
          {recentSearchQueries.length > 0 && (
            <RecentSearches
              list={recentSearchQueries}
              onSubmit={onSubmit}
              className={cn(!isSuggestions && 'grui mb-7')}
            />
          )}
          {Object.keys(updatedSuggestions).map((key, groupIndex) => {
            const groupSuggestions = updatedSuggestions[key]
            if (!groupSuggestions || groupSuggestions.length < 1) return null
            const first = groupSuggestions[0]
            if (first && first.isLoading && prevLabelSize < 1) return null
            return (
              <div
                key={`group-${key}`}
                className={styles.SearchSuggestions_group}
              >
                <div className={styles.SearchSuggestions_title}>
                  {KEY_TITLE_MAP[key] || key}
                </div>
                <div className={styles.SearchSuggestions_suggestions}>
                  {groupSuggestions.map((suggestion, index) => {
                    if (suggestion.isLoading) {
                      return (
                        <div
                          // It's ok to use index for loading list
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          className={styles.SearchSuggestions_loading}
                        >
                          {[...Array(prevLabelSize).keys()].map(labelIndex => (
                            <div
                              key={labelIndex}
                              className={styles.SearchSuggestions_suggestion}
                            >
                              <div
                                className={styles.suggestion}
                                css={indicator.styles.accentNeutral}
                              >
                                tag:
                              </div>
                              <div className={styles.hint}>loading</div>
                            </div>
                          ))}
                        </div>
                      )
                    }

                    return (
                      <KeyboardNavigator.Item
                        key={suggestion?.suggestion}
                        active={
                          isNeedingValue && index === 0 && groupIndex === 0
                        }
                        activeClassName={
                          styles.SearchSuggestions_activeSuggestion
                        }
                        onSelect={propFunc(onSelect, suggestion.searchQuery)}
                        onActivate={propFunc(onChange, suggestion.searchQuery)}
                      >
                        <Suggestion suggestion={suggestion} />
                      </KeyboardNavigator.Item>
                    )
                  })}
                </div>
              </div>
            )
          })}
          <SearchFeedback />
        </Scroller>
      </SUI>
    </KeyboardNavigator>
  )
}
